import { Injectable, Injector } from '@angular/core';
import { cloneDeep, set } from 'lodash-es';
import { BehaviorSubject, combineLatest, forkJoin, from, map, mergeMap, Observable, of, switchMap, take, tap } from 'rxjs';
import { IApiDatiBusiness, IDatiBusiness } from './models/interfaces/dati-business.interface';
import { Dati, IApiAssignmentGet, IRevisione } from './models/interfaces/revisione.interface';
import { JsonHelper } from '@core/helpers/json.helper';
import { HttpClient } from '@angular/common/http';
import { Revisione53State } from './store/revisione53.reducers';
import { Store } from '@ngrx/store';
import { Revisione53CoreSelectors } from './store/selectors/core.selectors';
import { format, parse } from 'date-fns';
import { IApiLocalSaveData, ILocalRemoteLastSave, ILocalSaveData } from './models/interfaces/local-remote-last-save.interface';
import { IDbAllegatoBase64 } from '@core/models/interfaces/indexeddb.interface';
import { revisione53CoreHelpers } from './components/main-container/revisione53-main-container.helpers';
import { INavbarLink } from '@shared/models/interfaces/navbar-link.interface';
import { IndexedDBService } from '@core/services/indexeddb.service';
import { Revisione53CoreActions } from './store/revisione53.actions';
import { ToastrService } from 'ngx-toastr';
import { CryptoService } from '@core/services/crypto.service';
import { AltreNotLibriSocFormService } from './forms/altre-not-libri-soc/altre-not-libri-soc-form.service';
import { AmminControlloFormService } from './forms/amm-controllo/ammin-controllo-form.service';
import { BaseSocialeFormService } from './forms/base-sociale/base-sociale-form.service';
import { ConclusioniFormService } from './forms/conclusioni/conclusioni-form.service';
import { ContribBiennaleFormService } from './forms/contrib-biennale/contrib-biennale-form.service';
import { ContribLegge5992FormService } from './forms/contrib-legge-59-92/contrib-legge-59-92-form.service';
import { DatiBilancioBisFormService } from './forms/dati-bilancio-bis/dati-bilancio-bis-form.service';
import { DatiBilancioFormService } from './forms/dati-bilancio/dati-bilancio-form.service';
import { DatiIdentificativiFormService } from './forms/dati-identificativi/dati-identificativi-form.service';
import { NaturaMutualisticaFormService } from './forms/natura-mutualistica/natura-mutualistica-form.service';
import { SchedaSettoreFormService } from './forms/scheda-settore/scheda-settore-form.service';
import { ScopoSocialeFormService } from './forms/scopo-sociale/scopo-sociale-form.service';
import { SezIIAccertamentoFormService } from './forms/sez-II-accertamento/sez-ii-accertamento-form.service';
import { environment } from '@env';

@Injectable()
export class Revisione53Service {
    navbarLinksRilevazione: INavbarLink[];
    navbarLinksAccertamento: INavbarLink[];
    navbarLinksSchedaSettore: INavbarLink[];

    private _isCurrentlySaving = false;

    constructor(
        private http: HttpClient,
        private store: Store<Revisione53State>,
        private injector: Injector,
        private indexedDb: IndexedDBService,
        private toastr: ToastrService,
        private cryptoService: CryptoService,
        private altreNotLibriSocFormService: AltreNotLibriSocFormService,
        private conclusioniFormService: ConclusioniFormService,
        private contribLegge5992FormService: ContribLegge5992FormService,
        private contribBiennaleFormService: ContribBiennaleFormService,
        private amminControlloFormService: AmminControlloFormService,
        private scopoSocialeFormService: ScopoSocialeFormService,
        private datiBilancioFormService: DatiBilancioFormService,
        private datiBilancioBisFormService: DatiBilancioBisFormService,
        private baseSocialeFormService: BaseSocialeFormService,
        private naturaMutualisticaFormService: NaturaMutualisticaFormService,
        private datiIdentificativiFormService: DatiIdentificativiFormService
    ) {}

    public get isCurrentlySaving(): boolean {
        return this._isCurrentlySaving;
    }

    setNavItems(): void {
        this.setNavbarLinksRilevazione();
        this.setNavbarLinksSchedaSettore();
    }

    load(): Observable<{ revisione: IRevisione; datiBusiness: IDatiBusiness }> {
        return this.datiBusiness$().pipe(
            switchMap((datiBusiness) =>
                this.revisione$().pipe(
                    map(
                        (revisione) =>
                            ({
                                revisione,
                                datiBusiness,
                            } as { revisione: IRevisione; datiBusiness: IDatiBusiness })
                    )
                )
            )
        );
    }

    saveInLocal(showSpinner = true): Observable<any> {
        return this.save(false, showSpinner);
    }

    save(upload: boolean, showSpinner = true): Observable<any> {
        if (upload) {
            this.store.dispatch(Revisione53CoreActions.autosaveDataUltSalvataggio());
            return this.store.select(Revisione53CoreSelectors.getRevisione).pipe(
                take(1),
                tap(() => (this._isCurrentlySaving = true)),
                map(this.formatLastSaveDate),
                switchMap((revisione) => this._postAssignmentSet(revisione, '', showSpinner)),
                tap((result) => {
                    if (result) {
                        this.store.dispatch(Revisione53CoreActions.updateDataUltSalvataggioAndMd5({ localSaveData: result }));
                    }
                }),
                tap(() => (this._isCurrentlySaving = false))
            );
        } else {
            return this.store.select(Revisione53CoreSelectors.getRevisioneAndDatiBusiness).pipe(
                take(1),
                tap(() => (this._isCurrentlySaving = true)),
                map((state) => ({ ...state, revisione: this.formatLastSaveDate(state.revisione) })),
                switchMap((state) => this.downloadJson(state)),
                tap(() => (this._isCurrentlySaving = false))
            );
        }
    }

    public sendVerbale(revisione: any, showSpinner = true): Observable<ILocalSaveData> {
        revisione.xml = JsonHelper.restoreRevisioneJson(revisione);
        return this._postAssignmentSet(revisione, 'invio', showSpinner);
    }

    public verificaVerbale(revisione: any, showSpinner = true): Observable<ILocalSaveData> {
        return this._postAssignmentSet(revisione, 'verifica', showSpinner);
    }

    public sendBilancioAllegato(idAssegnazione: number, documentType: string, bilancioBase64: string, showSpinner = true): Observable<Dati> {
        const options = { headers: { showSpinner: String(showSpinner) } };
        const dto = { assignmentIdAppCore: idAssegnazione, documentType, bilancioBase64 };
        return this.http.post<any>(`${environment.apiBaseUrl}/api/services/app/Assignment/SetDatiBilancio`, dto, options).pipe(map((res) => res.result));
    }

    public sendDocumentoPresaInCarico(documento: any, showSpinner = true): Observable<ILocalSaveData> {
        const options = { headers: { showSpinner: String(showSpinner) } };
        return this.http
            .post<IApiLocalSaveData>(`${environment.apiBaseUrl}/api/services/app/AssignmentAppCore/InvioDocumentoPresaInCarico`, documento, options)
            .pipe(map((res) => res.result));
    }

    public datiBusiness$(): Observable<IDatiBusiness> {
        const assignmentIdAppCore = sessionStorage.getItem('verbale_idAssegnazione');
        return this.http
            .get<IApiDatiBusiness>(`${environment.apiBaseUrl}/api/services/app/Assignment/GetRevisionBusinessData`, { params: { assignmentIdAppCore } })
            .pipe(map((res) => res.result));
    }

    public revisione$(showSpinner = true): Observable<IRevisione> {
        const assignmentIdAppCore = sessionStorage.getItem('verbale_idAssegnazione');
        const options = {
            headers: { showSpinner: String(showSpinner) },
            params: { assignmentIdAppCore, documentType: 'VERB' },
        };
        return this.http.get<IApiAssignmentGet>(`${environment.apiBaseUrl}/api/services/app/Assignment/Get`, options).pipe(
            map((res) => res.result),
            map((revisione) => JsonHelper.processRevisioneJson(revisione) as IRevisione),
            map(this.formatLastSaveDate)
        );
    }

    localRemoteLastSave$(): Observable<ILocalRemoteLastSave> {
        const emptyStatus = { azione: '', stato: '', messaggio: '' };
        const remoteLastSave$ = this.revisione$(false).pipe(map((r) => ({ dataultimosalvataggio: Number(r.dataultimosalvataggio), md5: r.md5, ...emptyStatus })));
        const localLastSave$ = this.store
            .select(Revisione53CoreSelectors.getLastSave)
            .pipe(map((s) => ({ dataultimosalvataggio: s.dataultimosalvataggio.getTime(), md5: s.md5, ...emptyStatus })));
        return combineLatest([remoteLastSave$, localLastSave$]).pipe(
            take(1),
            map(([remote, local]) => ({
                remote,
                local,
                same: remote.md5 === local.md5 && remote.dataultimosalvataggio === local.dataultimosalvataggio,
            }))
        );
    }

    public setNavbarLinksAccertamento(revisione: IRevisione): void {
        const verbale = revisione?.dati.my_Verbale;
        const noAccertamento = !(verbale.my_StatoVerbale === '1' && verbale.my_StatoAccertamento === '1' && verbale.my_SezioneI__Rilevazione.my_Conclusioni.my_CkProposte === '3');
        this.navbarLinksAccertamento = [
            {
                title: 'Sez. II - Accertamento',
                path: 'sez-II-accertamento',
                isValid$: this.injector.get(SezIIAccertamentoFormService).isValid$,
                isDisabled$: new BehaviorSubject<boolean>(noAccertamento),
            },
        ];
    }

    getRevisioneWithBase64Files(options: { revisione?: IRevisione; removeFileNameAndDetails?: boolean }): Observable<IRevisione> {
        const revisione$ = options.revisione ? of(options.revisione) : this.store.select(Revisione53CoreSelectors.getRevisione).pipe(take(1));
        return revisione$.pipe(
            mergeMap((revisione) =>
                this.getFilesFromDb(revisione).pipe(
                    map((allegatiBase64) => {
                        if (!allegatiBase64) return revisione;
                        const clone: IRevisione = cloneDeep(revisione);
                        allegatiBase64.forEach((a) => {
                            const [prefix, data] = a.base64File.split(',');
                            set(clone, a.position, options.removeFileNameAndDetails ? data : `${prefix};${a.fileName},${data}`);
                        });
                        return clone;
                    })
                )
            )
        );
    }

    xmlToJson(xmlBase64: string, showSpinner: boolean): Observable<any> {
        const body = { base64: xmlBase64 };
        const options = { headers: { showSpinner: String(showSpinner) } };
        return this.http
            .post<any>(`${environment.apiBaseUrl}/api/services/app/Assignment/Xml2Json`, body, options)
            .pipe(map((res) => (JsonHelper.processRevisioneJson(res.result) as any).my_Verbale));
    }

    private _postAssignmentSet(revisione: any, azione: string, showSpinner: boolean): Observable<ILocalSaveData> {
        const options = {
            headers: { showSpinner: String(showSpinner) },
            params: { assignmentIdAppCore: revisione.idassegnazione, azione },
        };
        return this.http.post<IApiLocalSaveData>(`${environment.apiBaseUrl}/api/services/app/Assignment/Set`, revisione, options).pipe(map((res) => res.result));
    }

    private formatLastSaveDate = (revisione: IRevisione): IRevisione => {
        if (!revisione) return revisione;
        if (!isNaN(new Date(Number(revisione.dataultimosalvataggio)).getTime())) {
            return revisione;
        }
        const revisioneClone: IRevisione = cloneDeep(revisione);
        revisioneClone.dataultimosalvataggio = parse(revisione.dataultimosalvataggio?.toString(), 'dd/MM/yyyy', new Date()).getTime();
        return revisioneClone;
    };

    private downloadJson(state: { revisione: IRevisione; datiBusiness: IDatiBusiness }): Observable<void> {
        return new Observable<void>((subscriber) => {
            const fail = (msg: string): void => {
                this.toastr.error(msg, 'Errore');
                subscriber.next();
                subscriber.complete();
            };
            const encryptPsw = prompt('Inserisci una password per proteggere il backup che verrà scaricato sul tuo computer: ');
            const encryptPswDouble = prompt('Cortesemente reinserisci la stessa password per evitare errori di digitazione: ');
            if (!encryptPsw) {
                fail('È obbligatorio impostare una password per proseguire');
                return;
            }
            if (!encryptPswDouble) {
                fail('È obbligatorio digitare la stessa password una seconda volta per proseguire');
                return;
            }
            if (encryptPsw !== encryptPswDouble) {
                fail('Le due password non coincidono. Clicca nuovamente sul pulsante "Salva su PC" per riprovare.');
                return;
            }

            const jsonEncrypted = this.cryptoService.encrypt(JSON.stringify(state), encryptPsw);
            const dlAnchorElem = document.createElement('a');
            dlAnchorElem.setAttribute('href', window.URL.createObjectURL(new Blob([jsonEncrypted], { type: 'text/plain' })));
            dlAnchorElem.setAttribute('download', `revisione53-${format(new Date(), 'yyyyMMdd-HHmmss')}.backup`);
            dlAnchorElem.click();
            dlAnchorElem.remove();
            subscriber.next();
            subscriber.complete();
        });
    }

    /* Usata per il watermark "bozza" in stampa (REM-20): equivalente NCR di ceErrore nel VBS
    calcolaStampabileSezioneI, che aggregava gli errori di tutte le sezioni della Sezione I. */
    getIsSezioneIValid$(): Observable<boolean> {
        const allFormsStatuses$: Observable<boolean>[] = this.navbarLinksRilevazione.filter((e) => !!e.isValid$).map((e) => e.isValid$);
        return combineLatest(allFormsStatuses$).pipe(map((allFormsStatuses) => !allFormsStatuses.includes(false)));
    }

    private setNavbarLinksRilevazione(): void {
        const link = (title: string, path: string, svc: Pick<INavbarLink, 'isValid$' | 'form'>): INavbarLink => ({ title, path, isValid$: svc.isValid$, form: svc.form });

        this.navbarLinksRilevazione = [
            link('Dati identificativi', 'dati-identificativi', this.datiIdentificativiFormService),
            link('Natura mutualistica', 'natura-mutualistica', this.naturaMutualisticaFormService),
            link('Base sociale', 'base-sociale', this.baseSocialeFormService),
            link('Dati Bilancio', 'dati-bilancio-bis', this.datiBilancioBisFormService),
            link('Bilancio', 'dati-bilancio', this.datiBilancioFormService),
            link('Scopo sociale', 'scopo-sociale', this.scopoSocialeFormService),
            {
                title: 'Ammin. e controllo',
                path: 'ammin-controllo',
                isValid$: this.amminControlloFormService.isValid$,
                subLinks: this.amminControlloFormService.navbarLinks,
            },
            link('Contributo biennale', 'contrib-biennale', this.contribBiennaleFormService),
            link('Contrib. Legge 59-92', 'contrib-legge-59-92', this.contribLegge5992FormService),
            link('Altre Notizie/Libri Soc', 'altre-not-libri-soc', this.altreNotLibriSocFormService),
            link('Conclusioni', 'conclusioni', this.conclusioniFormService),
        ];
    }

    private setNavbarLinksSchedaSettore(): void {
        const schedaSettoreFormService = this.injector.get(SchedaSettoreFormService);
        this.navbarLinksSchedaSettore = [
            {
                title: 'Scheda di settore',
                path: 'schede-settore',
                isValid$: schedaSettoreFormService.isValid$,
                subLinks: schedaSettoreFormService.navbarLinks,
            },
        ];
    }

    private getFilesFromDb(revisione: IRevisione): Observable<IDbAllegatoBase64[] | undefined> {
        const allegatiPositionAndId = revisione53CoreHelpers.getAllegatiPositionAndValue(revisione);
        const ids = allegatiPositionAndId.map((a) => a.value);
        if (ids.length === 0) return of(undefined);

        return this.indexedDb.bulkGet(ids).pipe(
            switchMap((allegati) =>
                from(
                    Promise.all(
                        allegati
                            .filter((a) => !!a)
                            .map(
                                (a) =>
                                    new Promise<IDbAllegatoBase64>((resolve) => {
                                        const reader = new FileReader();
                                        reader.readAsDataURL(a.file);
                                        reader.onload = () => resolve({ id: a.id, fileName: a.file.name, base64File: reader.result as string });
                                    })
                            )
                    )
                )
            ),
            map((files) =>
                files.map((file) => ({
                    ...file,
                    position: allegatiPositionAndId.find((a) => a.value === file.id).position,
                }))
            )
        );
    }
}
